import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Edit2, Megaphone, BadgePercent } from "lucide-react";

const inp = "w-full border border-ink-200 px-3 py-2";
const AUD = { all: "Herkes", dealers: "Sadece bayiler", customers: "Sadece perakende" };
const LEVEL = { info: "bg-b2b", success: "bg-green-600", warning: "bg-brand" };

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "md:col-span-2" : ""}`}><span className="text-xs font-semibold mb-1 block">{label}</span>{children}</label>
);

const dateRange = (x) => (x.start_date || x.end_date) ? `${x.start_date || "…"} → ${x.end_date || "…"}` : "Süresiz";

function useCrud(path, empty) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get(path).then(r => setItems(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (payload) => {
    try {
      if (form.id) await api.put(`${path}/${form.id}`, payload); else await api.post(path, payload);
      toast.success("Kaydedildi"); setForm(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Kaydedilemedi"); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`${path}/${id}`); toast.success("Silindi"); load(); };
  const toggle = async (x) => { const { id, created_at, ...rest } = x; await api.put(`${path}/${id}`, { ...rest, active: !x.active }); load(); };
  return { items, form, setForm, save, del, toggle, startNew: () => setForm({ ...empty }) };
}

function Row({ x, title, meta, onEdit, onDel, onToggle, testid }) {
  return (
    <div data-testid={testid} className="p-4 flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="font-bold flex items-center gap-2 flex-wrap">{title}
          <span className={`text-[10px] px-2 py-0.5 font-bold ${x.active ? "bg-green-100 text-green-700" : "bg-ink-100 text-ink-500"}`}>{x.active ? "AKTİF" : "PASİF"}</span>
        </div>
        <div className="text-xs text-ink-500 mt-1">{meta}</div>
      </div>
      <div className="flex gap-2">
        <button data-testid={`${testid}-toggle`} onClick={onToggle} className="kz-btn-outline text-xs py-1.5 px-3">{x.active ? "Durdur" : "Aktifleştir"}</button>
        <button data-testid={`${testid}-edit`} onClick={onEdit} className="kz-btn-outline text-xs py-1.5 px-3"><Edit2 size={12}/></button>
        <button data-testid={`${testid}-delete`} onClick={onDel} className="text-red-600 border border-red-200 text-xs px-3 py-1.5"><Trash2 size={14}/></button>
      </div>
    </div>
  );
}

const CAMP_EMPTY = { name: "", description: "", discount_pct: 10, target: "all", target_value: "", audience: "all", start_date: "", end_date: "", active: true };

export function CampaignsTab({ cats }) {
  const c = useCrud("/admin/campaigns", CAMP_EMPTY);
  const [brands, setBrands] = useState([]);
  useEffect(() => { api.get("/admin/brands").then(r => setBrands(r.data)); }, []);
  const f = c.form;
  const set = (k) => (e) => c.setForm({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value, ...(k === "target" ? { target_value: "" } : {}) });
  const targetLabel = (x) => x.target === "category" ? `Kategori: ${cats.find(k => k.id === x.target_value)?.name || "?"}` : x.target === "brand" ? `Marka: ${x.target_value}` : "Tüm ürünler";

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-ink-500 max-w-xl">Kampanya indirimi bayi iskontosunun üzerine ek olarak uygulanır. Aynı ürüne birden fazla kampanya denk gelirse en yüksek oran geçerlidir.</p>
        <button data-testid="new-campaign-btn" onClick={c.startNew} className="kz-btn-primary"><Plus size={16}/> Yeni Kampanya</button>
      </div>
      {f && (
        <form data-testid="campaign-form" onSubmit={(e) => { e.preventDefault(); c.save({ ...f, discount_pct: parseFloat(f.discount_pct) }); }} className="kz-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Kampanya Adı" wide><input data-testid="campaign-name" required value={f.name} onChange={set("name")} className={inp}/></Field>
          <Field label="Açıklama" wide><input value={f.description} onChange={set("description")} className={inp}/></Field>
          <Field label="İndirim (%)"><input data-testid="campaign-discount" type="number" step="0.5" min="0.5" max="99" required value={f.discount_pct} onChange={set("discount_pct")} className={inp}/></Field>
          <Field label="Kimler görsün?">
            <select data-testid="campaign-audience" value={f.audience} onChange={set("audience")} className={inp}>{Object.entries(AUD).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          </Field>
          <Field label="Hangi ürünler?">
            <select data-testid="campaign-target" value={f.target} onChange={set("target")} className={inp}>
              <option value="all">Tüm ürünler</option><option value="category">Kategori</option><option value="brand">Marka</option>
            </select>
          </Field>
          <Field label={f.target === "brand" ? "Marka" : "Kategori"}>
            {f.target === "all" ? <input disabled value="—" className={`${inp} bg-ink-100`}/> : (
              <select data-testid="campaign-target-value" required value={f.target_value} onChange={set("target_value")} className={inp}>
                <option value="">Seçin</option>
                {f.target === "category" ? cats.map(k => <option key={k.id} value={k.id}>{k.name}</option>) : brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
          </Field>
          <Field label="Başlangıç"><input type="date" value={f.start_date} onChange={set("start_date")} className={inp}/></Field>
          <Field label="Bitiş"><input type="date" value={f.end_date} onChange={set("end_date")} className={inp}/></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={set("active")}/> Aktif</label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => c.setForm(null)} className="kz-btn-outline text-sm py-2 px-4">İptal</button>
            <button data-testid="campaign-save" type="submit" className="kz-btn-primary text-sm py-2 px-4">Kaydet</button>
          </div>
        </form>
      )}
      <div className="kz-card divide-y divide-ink-200">
        {c.items.length === 0 ? <div className="p-10 text-center text-ink-500"><BadgePercent className="mx-auto mb-2 text-ink-300"/>Henüz kampanya yok</div> :
          c.items.map(x => (
            <Row key={x.id} x={x} testid={`campaign-${x.id}`} title={<>{x.name} <span className="text-brand font-black">-%{x.discount_pct}</span></>}
              meta={`${targetLabel(x)} · ${AUD[x.audience]} · ${dateRange(x)}`}
              onEdit={() => c.setForm(x)} onDel={() => c.del(x.id)} onToggle={() => c.toggle(x)}/>
          ))}
      </div>
    </div>
  );
}

const ANN_EMPTY = { title: "", message: "", audience: "all", level: "info", start_date: "", end_date: "", active: true };

export function AnnouncementsTab() {
  const c = useCrud("/admin/announcements", ANN_EMPTY);
  const f = c.form;
  const set = (k) => (e) => c.setForm({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <p className="text-sm text-ink-500">Aktif duyurular sitenin üst kısmında ve bayi panelinde gösterilir.</p>
        <button data-testid="new-announcement-btn" onClick={c.startNew} className="kz-btn-primary"><Plus size={16}/> Yeni Duyuru</button>
      </div>
      {f && (
        <form data-testid="announcement-form" onSubmit={(e) => { e.preventDefault(); c.save(f); }} className="kz-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Başlık" wide><input data-testid="announcement-title" required value={f.title} onChange={set("title")} className={inp}/></Field>
          <Field label="Mesaj" wide><textarea data-testid="announcement-message" required rows={2} value={f.message} onChange={set("message")} className={inp}/></Field>
          <Field label="Kimler görsün?">
            <select data-testid="announcement-audience" value={f.audience} onChange={set("audience")} className={inp}><option value="all">Tüm ziyaretçiler</option><option value="dealers">Sadece bayiler</option></select>
          </Field>
          <Field label="Renk">
            <select value={f.level} onChange={set("level")} className={inp}><option value="info">Mavi (bilgi)</option><option value="success">Yeşil (müjde)</option><option value="warning">Turuncu (önemli)</option></select>
          </Field>
          <Field label="Başlangıç"><input type="date" value={f.start_date} onChange={set("start_date")} className={inp}/></Field>
          <Field label="Bitiş"><input type="date" value={f.end_date} onChange={set("end_date")} className={inp}/></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={set("active")}/> Aktif</label>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => c.setForm(null)} className="kz-btn-outline text-sm py-2 px-4">İptal</button>
            <button data-testid="announcement-save" type="submit" className="kz-btn-primary text-sm py-2 px-4">Kaydet</button>
          </div>
        </form>
      )}
      <div className="kz-card divide-y divide-ink-200">
        {c.items.length === 0 ? <div className="p-10 text-center text-ink-500"><Megaphone className="mx-auto mb-2 text-ink-300"/>Henüz duyuru yok</div> :
          c.items.map(x => (
            <Row key={x.id} x={x} testid={`announcement-${x.id}`} title={<><span className={`w-2 h-2 inline-block ${LEVEL[x.level]}`}/> {x.title}</>}
              meta={`${x.message.slice(0, 90)} · ${x.audience === "dealers" ? "Bayiler" : "Herkes"} · ${dateRange(x)}`}
              onEdit={() => c.setForm(x)} onDel={() => c.del(x.id)} onToggle={() => c.toggle(x)}/>
          ))}
      </div>
    </div>
  );
}
